import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import AuthService from "./AuthService";
import { getInternalBranding } from "./Dashboards/dashboardService";

function SidebarItem({
  to,
  icon,
  label,
  isOpen,
  end = false,
  onNavigate,
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `netri-sidebar-link ${isActive ? "active" : ""}`
      }
      title={!isOpen ? label : undefined}
      onClick={onNavigate}
    >
      <span className="netri-sidebar-link__icon">
        <i className={`bi ${icon}`}></i>
      </span>

      {isOpen && (
        <span className="netri-sidebar-link__label">
          {label}
        </span>
      )}
    </NavLink>
  );
}

function PlaceholderItem({
  icon,
  label,
  isOpen,
}) {
  return (
    <button
      type="button"
      className="netri-sidebar-link"
      title={!isOpen ? label : undefined}
    >
      <span className="netri-sidebar-link__icon">
        <i className={`bi ${icon}`}></i>
      </span>

      {isOpen && (
        <span className="netri-sidebar-link__label">
          {label}
        </span>
      )}
    </button>
  );
}

function Sidebar({ isOpen, onNavigate }) {
  const user = AuthService.getCurrentUser();
  const [brand, setBrand] = useState(null);

  useEffect(() => {
    let active = true;

    getInternalBranding()
      .then((result) => {
        if (active) setBrand(result);
      })
      .catch(() => {});

    return () => {
      active = false;
    };
  }, []);

  const permissions = Array.isArray(user?.permissions)
    ? user.permissions
    : [];

  const hasPermission = (...required) =>
    permissions.includes("*") ||
    required.some((permission) =>
      permissions.includes(permission)
    );

  const isSystemUser =
    user?.access_type === "SYSTEM_USER";

  const roleName = String(user?.role || "").trim().toUpperCase();
  const canViewManagementDashboard =
    !isSystemUser ||
    ["DIRECTOR", "SYSTEM ADMINISTRATOR"].includes(roleName);

  const home =
    user?.access_type === "POLICY_HOLDER"
      ? "/policyholder/dashboard"
      : user?.access_type === "AGENT"
      ? "/agent/dashboard"
      : canViewManagementDashboard
      ? "/dashboard"
      : "/notifications";

  const shortName =
    brand?.short_name ||
    brand?.company_name ||
    "Assurance Company";

  return (
    <aside
      className={`netri-sidebar ${
        isOpen ? "open" : "closed"
      }`}
    >
      <div className="netri-sidebar-brand">
        <div className="netri-sidebar-brand__logo">
          {brand?.logo_url ? (
            <img
              src={brand.logo_url}
              alt={brand.company_name || shortName}
            />
          ) : (
            <span>
              {(brand?.short_name || "NA")
                .slice(0, 2)
                .toUpperCase()}
            </span>
          )}
        </div>

        {isOpen && (
          <div className="netri-sidebar-brand__text">
            <strong>{shortName}</strong>
            <small>Powered by NetriAssure</small>
          </div>
        )}
      </div>

      <div className="netri-sidebar-scroll">
        <nav className="netri-sidebar-nav">
          <div className="netri-sidebar-section">
            {isOpen ? "OVERVIEW" : ""}
          </div>
          {canViewManagementDashboard && (
            <SidebarItem to={home} icon="bi-grid-1x2" label="Dashboard" isOpen={isOpen} onNavigate={onNavigate} end />
          )}
          <SidebarItem to="/notifications" icon="bi-bell" label="Notifications" isOpen={isOpen} onNavigate={onNavigate} />

          {user?.access_type === "PARTNER" && (
            <>
              <div className="netri-sidebar-section">{isOpen ? "ECOSURE PARTNER" : ""}</div>
              <SidebarItem to="/partner/ecosure" icon="bi-speedometer2" label="EcoSure Dashboard" isOpen={isOpen} onNavigate={onNavigate} end />
              <SidebarItem to="/partner/ecosure/clients" icon="bi-people" label="EcoSure Clients" isOpen={isOpen} onNavigate={onNavigate} />
              <SidebarItem to="/partner/ecosure/remittances" icon="bi-cash-stack" label="Remittances" isOpen={isOpen} onNavigate={onNavigate} />
              <SidebarItem to="/partner/ecosure/reports" icon="bi-file-earmark-bar-graph" label="Reports" isOpen={isOpen} onNavigate={onNavigate} />
            </>
          )}

          {user?.access_type === "AGENT" && (
            <>
              <div className="netri-sidebar-section">{isOpen ? "MY BUSINESS" : ""}</div>
              <SidebarItem to="/agent/proposals" icon="bi-file-earmark-plus" label="My Proposals" isOpen={isOpen} onNavigate={onNavigate} />
              <SidebarItem to="/agent/dashboard" icon="bi-cash-coin" label="My Commissions" isOpen={isOpen} onNavigate={onNavigate} />
            </>
          )}

          {user?.access_type === "POLICY_HOLDER" && (
            <>
              <div className="netri-sidebar-section">{isOpen ? "MY POLICY" : ""}</div>
              <SidebarItem to="/policyholder/dashboard" icon="bi-person-vcard" label="My Policy & Members" isOpen={isOpen} onNavigate={onNavigate} />
              <SidebarItem to="/policyholder/dashboard" icon="bi-receipt" label="Payments & Receipts" isOpen={isOpen} onNavigate={onNavigate} />
              <SidebarItem to="/policyholder/dashboard" icon="bi-heart-pulse" label="Claims & Documents" isOpen={isOpen} onNavigate={onNavigate} />
            </>
          )}

          {isSystemUser && (
            <>
              {(hasPermission("view_audit_log")) && (
                <SidebarItem to="/audit-trail" icon="bi-clock-history" label="Audit Trail" isOpen={isOpen} onNavigate={onNavigate} />
              )}
              <div className="netri-sidebar-section">{isOpen ? "ASSURANCE OPERATIONS" : ""}</div>
              {(hasPermission("view_proposals")) && <SidebarItem to="/proposals" icon="bi-file-earmark-plus" label="Proposals" isOpen={isOpen} onNavigate={onNavigate} />}
              {(hasPermission("manage_policy_members")) && <SidebarItem to="/members" icon="bi-people" label="Members" isOpen={isOpen} onNavigate={onNavigate} />}
              {(hasPermission("view_policies")) && <SidebarItem to="/policies" icon="bi-file-earmark-text" label="Policies" isOpen={isOpen} onNavigate={onNavigate} />}
              {(hasPermission("edit_policies")) && <SidebarItem to="/policy-maintenance" icon="bi-tools" label="Policy Maintenance" isOpen={isOpen} onNavigate={onNavigate} />}
              {(hasPermission("view_claims")) && <SidebarItem to="/claims" icon="bi-heart-pulse" label="Claims" isOpen={isOpen} onNavigate={onNavigate} />}

              <div className="netri-sidebar-section">{isOpen ? "FINANCE & REPORTING" : ""}</div>
              {(hasPermission("view_commissions")) && <SidebarItem to="/commissions" icon="bi-cash-coin" label="Commissions" isOpen={isOpen} onNavigate={onNavigate} />}
              {(hasPermission("view_billing", "view_accounts")) && <SidebarItem to="/finance" icon="bi-bank" label="Finance & Transactions" isOpen={isOpen} onNavigate={onNavigate} />}
              {(hasPermission("view_accounts", "view_financial_reports")) && <SidebarItem to="/expenditure" icon="bi-wallet2" label="Cash & Expenditure" isOpen={isOpen} onNavigate={onNavigate} />}
              {(hasPermission("manage_ecosure")) && <SidebarItem to="/ecosure" icon="bi-shield-plus" label="EcoSure Control" isOpen={isOpen} onNavigate={onNavigate} />}
              {(hasPermission("manage_ecosure")) && <SidebarItem to="/bulk-imports" icon="bi-file-earmark-spreadsheet" label="Bulk Imports" isOpen={isOpen} onNavigate={onNavigate} />}
              {(hasPermission("view_operational_reports", "view_financial_reports")) && <SidebarItem to="/insurance-reports" icon="bi-file-earmark-bar-graph" label="Insurance Reports" isOpen={isOpen} onNavigate={onNavigate} />}

              {(hasPermission("view_dashboard")) && (
                <>
                  <div className="netri-sidebar-section">{isOpen ? "INTELLIGENCE" : ""}</div>
                  <SidebarItem to="/assurance-ai" icon="bi-stars" label="NetriAssure Intelligence" isOpen={isOpen} onNavigate={onNavigate} />
                </>
              )}

              {hasPermission(
                "manage_users",
                "manage_roles",
                "manage_general_settings",
                "manage_branches"
              ) && (
                <>
                  <div className="netri-sidebar-section">
                    {isOpen ? "ADMINISTRATION" : ""}
                  </div>
                  <SidebarItem to="/communications" icon="bi-envelope-paper" label="Communications" isOpen={isOpen} onNavigate={onNavigate} />
                  <SidebarItem
                    to="/settings"
                    icon="bi-gear"
                    label="System Settings"
                    isOpen={isOpen}
                    onNavigate={onNavigate}
                  />
                </>
              )}
            </>
          )}
        </nav>
      </div>

      <div className="netri-sidebar-footer">
        <div className="netri-sidebar-footer__mark">
          <i className="bi bi-shield-check"></i>
        </div>

        {isOpen && (
          <div>
            <strong>Secure access</strong>
            <span>NetriAssure platform</span>
          </div>
        )}
      </div>
    </aside>
  );
}

export default Sidebar;
